import type { GpsPoint } from '../domain/telemetry/processed/GpsPoint'
import type { AccelerationPoint } from '../domain/telemetry/processed/Acceleration'
import {
  ACCEL_CONSISTENCY_WINDOW_S,
  ACCEL_CONSISTENCY_MIN_TREND_MS2,
  ACCEL_CONSISTENCY_MIN_SIGNAL_MS2,
  ACCEL_CONSISTENCY_STRONG_TREND_MS2,
  ACCEL_CONSISTENCY_RELATIVE_TREND_MS2,
  ACCEL_CONSISTENCY_RELATIVE_SIGNAL_FRACTION,
  ACCEL_CONSISTENCY_SUSTAIN_S,
} from '../analysis/config/accelConsistency'

/**
 * GPS speed-trend acceleration around a point: central difference over a
 * ±windowS time window. Windowed to be robust against single-sample GPS
 * speed jitter (unlike an adjacent-point derivative).
 */
export function gpsSpeedTrendMs2(
  gpsPoints: GpsPoint[],
  index: number,
  windowS: number = ACCEL_CONSISTENCY_WINDOW_S
): number | null {
  const t0 = gpsPoints[index].timestamp.getTime()
  let back = index
  while (
    back > 0 &&
    t0 - gpsPoints[back - 1].timestamp.getTime() <= windowS * 1000
  ) {
    back--
  }
  let fwd = index
  while (
    fwd < gpsPoints.length - 1 &&
    gpsPoints[fwd + 1].timestamp.getTime() - t0 <= windowS * 1000
  ) {
    fwd++
  }
  if (back === fwd) return null
  const deltaTimeS =
    (gpsPoints[fwd].timestamp.getTime() - gpsPoints[back].timestamp.getTime()) /
    1000
  if (deltaTimeS <= 0) return null
  const deltaSpeedMs =
    (gpsPoints[fwd].speedKmh - gpsPoints[back].speedKmh) / 3.6
  return deltaSpeedMs / deltaTimeS
}

/**
 * Sign-aware physical-consistency check. flags[i] === true means point i's
 * longitudinal acceleration contradicts the GPS speed trend:
 * - sign mismatch while both signals are non-trivial, or
 * - a near-zero fused signal against an unmistakable GPS trend, or
 * - a fused magnitude under a credible fraction of a clear trend, but only
 *   when that condition holds for a SUSTAINED run of consecutive points
 *   (>= ACCEL_CONSISTENCY_SUSTAIN_S) — a single-sample dip is GPS-noise
 *   jitter in the trend, not a real attenuation defect, and gets ignored.
 */
export function validateAccelConsistency(
  gpsPoints: GpsPoint[],
  acceleration: AccelerationPoint[]
): boolean[] {
  const n = acceleration.length
  const signMismatch: boolean[] = new Array(n).fill(false)
  const absoluteMissing: boolean[] = new Array(n).fill(false)
  const relativeCandidate: boolean[] = new Array(n).fill(false)

  for (let i = 0; i < n; i++) {
    if (i >= gpsPoints.length) continue
    const trend = gpsSpeedTrendMs2(gpsPoints, i)
    if (trend === null) continue
    const fused = acceleration[i].longitudinalMs2
    signMismatch[i] =
      Math.abs(trend) >= ACCEL_CONSISTENCY_MIN_TREND_MS2 &&
      Math.abs(fused) >= ACCEL_CONSISTENCY_MIN_SIGNAL_MS2 &&
      Math.sign(fused) !== Math.sign(trend)
    absoluteMissing[i] =
      Math.abs(trend) >= ACCEL_CONSISTENCY_STRONG_TREND_MS2 &&
      Math.abs(fused) < ACCEL_CONSISTENCY_MIN_SIGNAL_MS2
    relativeCandidate[i] =
      Math.abs(trend) >= ACCEL_CONSISTENCY_RELATIVE_TREND_MS2 &&
      Math.abs(fused) <
        ACCEL_CONSISTENCY_RELATIVE_SIGNAL_FRACTION * Math.abs(trend)
  }

  const sustainedRelative = flagSustainedRuns(
    relativeCandidate,
    gpsPoints,
    ACCEL_CONSISTENCY_SUSTAIN_S
  )

  return acceleration.map(
    (_, i) => signMismatch[i] || absoluteMissing[i] || sustainedRelative[i]
  )
}

/**
 * Promotes a per-point candidate boolean array to flags, but only for points
 * inside a maximal run of consecutive candidates whose time span (first to
 * last point in the run) is at least sustainS seconds. Shorter runs — including
 * isolated single points — are dropped.
 */
function flagSustainedRuns(
  candidate: boolean[],
  gpsPoints: GpsPoint[],
  sustainS: number
): boolean[] {
  const n = candidate.length
  const flags: boolean[] = new Array(n).fill(false)
  let runStart: number | null = null

  for (let i = 0; i <= n; i++) {
    const isCandidate = i < n && candidate[i]
    if (isCandidate) {
      if (runStart === null) runStart = i
      continue
    }
    if (runStart !== null) {
      const runEnd = i - 1
      const durationS =
        (gpsPoints[runEnd].timestamp.getTime() -
          gpsPoints[runStart].timestamp.getTime()) /
        1000
      if (durationS >= sustainS) {
        for (let j = runStart; j <= runEnd; j++) flags[j] = true
      }
      runStart = null
    }
  }

  return flags
}
